/**
 * Refresh Auth User UseCase
 *
 * Re-fetches the authenticated user from Firestore and persists the result
 * to encrypted local storage. Redux state stays in sync automatically via
 * `AuthUserListener`, which subscribes to CURRENT_USER storage changes.
 *
 * Mirrors Flutter's pattern (UsersRepository.getUserById -> saveLocalUser)
 * applied wherever the auth user's profile may have changed remotely
 * (e.g. after editing the profile).
 *
 * Errors are logged and swallowed — the use case returns null on failure so
 * callers don't have to wrap each call in try/catch just to keep navigating
 * past a refresh that isn't mission-critical.
 */

import type { UserEntity } from '@/domain/entities/user/userEntity';
import { getUserUseCase } from '@/domain/usecases/user/getUserUseCase';
import { storeCurrentUserToLocalStorageUseCase } from '@/domain/usecases/user/storeCurrentUserToLocalStorageUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class RefreshAuthUserUseCase {
  async execute(userId: string): Promise<UserEntity | null> {
    try {
      const user = await getUserUseCase.execute(userId);
      if (!user) return null;

      await storeCurrentUserToLocalStorageUseCase.execute(user);
      return user;
    } catch (error) {
      appLogger.error(
        'Failed to refresh auth user',
        'RefreshAuthUserUseCase.execute', { error: error as Error },
      );
      return null;
    }
  }
}

export const refreshAuthUserUseCase = new RefreshAuthUserUseCase();
